import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  DEFAULT_TOASTER_LIFE,
  getWizardStepData,
  IWizardContext,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnButton,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { firstValueFrom, of, tap } from 'rxjs';
import type {
  StandDivisionAndFusionAdvanceValidationInputDto,
  StandDivisionAndFusionStandDetailDto,
  StandDivisionAndFusionStandLookupDto,
  StandDivisionAndFusionStandValidationInputDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-division-and-fusion/models';
import { StandGis } from '../../stand/stand-gis/stand-gis';
import { IDividedStandRow } from '../interfaces/IDividedStandRow';
import { ISelectPropertyStepData } from '../interfaces/ISelectPropertyStepData';
import { ISelectStandsToDivideStepData } from '../interfaces/ISelectStandsToDivideStepData';
import { StandDivisionAndFusion } from '../services/stand-division-and-fusion';
import { EMPTY_VALUE, IGNORED_BACKEND_ERROR_CODES, VALIDATION_ERROR_KEYS } from '../stand-division-and-fusion.config';
import { extractBackendErrorCode, extractBackendErrorMessage } from '../utils/backend-error';

type SelectValue = string | string[] | null;
type AreaInputValue = string | number | null;

@Component({
  selector: 'lib-step-select-stands-to-divide',
  standalone: true,
  imports: [LocalizationPipe, StandGis, ZnButton, ZnSingleSelect],
  templateUrl: './step-select-stands-to-divide.html',
})
export class StepSelectStandsToDivide
  implements WizardStepComponent<ISelectStandsToDivideStepData>
{
  readonly standSelect = viewChild('standSelect', { read: ZnSingleSelect });

  private readonly toaster = inject(ToasterService);
  private readonly standDivisionAndFusionService = inject(StandDivisionAndFusion);

  readonly localizationKeyPrefix = 'Geographic::StandDivisionAndFusion.StepSelectStandsToDivide';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly previousStepId = 1;

  readonly propertyId = signal<string | null>(null);
  readonly propertyName = signal<string | null>(null);
  readonly selectedStandId = signal<string | null>(null);
  readonly newAreaInput = signal<number | null>(null);
  readonly selectedRows = signal<IDividedStandRow[]>([]);
  readonly selectedStandDetail = signal<StandDivisionAndFusionStandDetailDto | null>(null);
  readonly loading = signal(false);

  readonly referenceRow = computed(() => this.selectedRows()[0] ?? null);
  readonly selectedStand = computed(() => this.standOptionsMap.get(this.selectedStandId() ?? '') ?? null);
  readonly currentArea = computed(
    () => this.selectedStandDetail()?.standArea ?? this.selectedStand()?.standArea ?? null,
  );
  readonly gisArea = computed(() => this.selectedStand()?.gisArea ?? null);
  readonly sectionedArea = computed(() => {
    const validatedSectionedArea = this.selectedStandDetail()?.sectionedArea;
    if (validatedSectionedArea !== null && validatedSectionedArea !== undefined) {
      return this.roundArea(validatedSectionedArea);
    }

    const currentArea = this.currentArea();
    const newArea = this.newAreaInput();

    if (
      currentArea === null ||
      newArea === null ||
      newArea <= 0
    ) {
      return null;
    }

    if (newArea === currentArea) {
      return 0;
    }

    if (newArea > currentArea) {
      return null;
    }

    return this.roundArea(currentArea - newArea);
  });
  readonly totalCurrentArea = computed(() =>
    this.roundArea(this.selectedRows().reduce((sum, row) => sum + row.currentArea, 0)),
  );
  readonly totalNewArea = computed(() =>
    this.roundArea(this.selectedRows().reduce((sum, row) => sum + row.newArea, 0)),
  );
  readonly totalSectionedArea = computed(() =>
    this.roundArea(this.selectedRows().reduce((sum, row) => sum + row.sectionedArea, 0)),
  );
  readonly canFetchGisArea = computed(() => !!this.selectedStand()?.name);
  readonly addDisabled = computed(
    () => !this.selectedStandId() || this.newAreaInput() === null || this.loading(),
  );

  private readonly standOptionsMap = new Map<string, StandDivisionAndFusionStandLookupDto>();

  readonly loadStands = (params?: ZnSelectQueryParams) => {
    const propertyId = this.numericPropertyId();
    if (!propertyId) {
      return of({ items: [], totalCount: 0 });
    }

    return this.standDivisionAndFusionService.searchStandOptions(propertyId, params).pipe(
      tap(response => {
        response.items.forEach(item => {
          this.standOptionsMap.set(item.key, item.lookup);
        });
      }),
    );
  };

  onActivate(context: IWizardContext): void {
    const activeStepId = context.activeStepId();
    const previousStepData: ISelectPropertyStepData | undefined = getWizardStepData(
      context,
      this.previousStepId,
    );
    const currentState: ISelectStandsToDivideStepData | undefined = getWizardStepData(
      context,
      activeStepId,
    );
    const nextPropertyId = previousStepData?.propertyId ?? currentState?.propertyId ?? null;
    const nextPropertyName = previousStepData?.propertyName ?? currentState?.propertyName ?? null;
    const shouldRestoreCurrentState =
      !!currentState &&
      !!nextPropertyId &&
      currentState.propertyId === nextPropertyId;

    context.patchStepState(activeStepId, {
      visited: true,
    });

    this.propertyId.set(nextPropertyId);
    this.propertyName.set(nextPropertyName);
    this.standOptionsMap.clear();
    this.selectedRows.set(
      shouldRestoreCurrentState
        ? this.normalizeRows(currentState.selectedStands ?? [])
        : [],
    );
    this.clearSelection();
  }

  onStandChange(value: SelectValue): void {
    const standId = this.toSingleSelectValue(value);
    this.selectedStandId.set(standId);
    this.selectedStandDetail.set(null);
  }

  onNewAreaInput(value: AreaInputValue): void {
    this.newAreaInput.set(this.parseArea(value));
  }

  onGisAreaUpdated(area: number): void {
    this.newAreaInput.set(this.parseArea(area));
  }

  async addStand(): Promise<void> {
    if (this.loading()) {
      return;
    }

    if (this.hasInvalidNewAreaAgainstCurrentArea()) {
      this.notifyError('NewAreaMustBeLowerThanCurrentArea');
      return;
    }

    const input = this.buildStandValidationInput();
    if (!input) {
      this.notifyError('SelectionRequired');
      return;
    }

    if (this.selectedRows().some(row => this.toNumericRowId(row.id) === input.standId)) {
      this.notifyError('StandAlreadySelected');
      this.clearSelection();
      return;
    }

    this.loading.set(true);

    try {
      const detail = await firstValueFrom(
        this.standDivisionAndFusionService.validateStand(input),
      );
      this.selectedStandDetail.set(detail);

      const row = this.toDividedStandRow(detail);
      if (!row) {
        return;
      }

      if (this.hasDifferentReferenceValues(row)) {
        this.notifyError('InvalidStand');
        return;
      }

      this.selectedRows.update(rows => this.normalizeRows([...rows, row]));
      this.clearSelection();
    } catch (error) {
      this.handleAddStandError(error);
    } finally {
      this.loading.set(false);
    }
  }

  removeStand(standId: number | string): void {
    const targetStandId = this.toNumericRowId(standId);
    if (targetStandId === null) {
      return;
    }

    this.selectedRows.update(rows =>
      this.normalizeRows(rows.filter(row => this.toNumericRowId(row.id) !== targetStandId)),
    );
  }

  async canLeave(): Promise<boolean> {
    if (this.selectedRows().length < 2) {
      this.notifyError('MinimumSelectionRequired');
      return false;
    }

    try {
      await firstValueFrom(
        this.standDivisionAndFusionService.ensureCanAdvance(
          this.buildAdvanceValidationInput(),
        ),
      );
      return true;
    } catch (error) {
      this.handleValidationError(error);
      return false;
    }
  }

  canLeaveSync(): boolean {
    return !this.loading();
  }

  hasMeaningfulData(): boolean {
    return (
      this.selectedRows().length > 0 ||
      !!this.selectedStandId() ||
      this.newAreaInput() !== null
    );
  }

  collect(context: IWizardContext): ISelectStandsToDivideStepData {
    const referenceRow = this.referenceRow();
    const data: ISelectStandsToDivideStepData = {
      propertyId: this.propertyId(),
      propertyName: this.propertyName(),
      selectedStands: this.selectedRows(),
      totalCurrentArea: this.totalCurrentArea(),
      totalNewArea: this.totalNewArea(),
      totalSectionedArea: this.totalSectionedArea(),
      referenceSpecies: referenceRow?.species ?? null,
      referenceLandContract: referenceRow?.landContract ?? null,
      referenceForestContract: referenceRow?.forestContract ?? null,
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: this.selectedRows().length >= 2,
      completed: true,
      data,
    });

    return data;
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return EMPTY_VALUE;
    }

    return value.toFixed(2).replace('.', ',');
  }

  private buildStandValidationInput():
    | StandDivisionAndFusionStandValidationInputDto
    | null {
    const propertyId = this.numericPropertyId();
    const standId = this.numericSelectedStandId();
    const newArea = this.newAreaInput();

    if (!propertyId || !standId || newArea === null) {
      return null;
    }

    return {
      propertyId,
      standId,
      newArea,
      selectedStandIds: this.selectedRows()
        .map(row => this.toNumericRowId(row.id))
        .filter((rowId): rowId is number => rowId !== null),
    };
  }

  private hasInvalidNewAreaAgainstCurrentArea(): boolean {
    const currentArea = this.currentArea();
    const newArea = this.newAreaInput();

    if (currentArea === null || newArea === null) {
      return false;
    }

    return newArea >= currentArea;
  }

  private buildAdvanceValidationInput(): StandDivisionAndFusionAdvanceValidationInputDto {
    return {
      selectedStandIds: this.selectedRows()
        .map(row => this.toNumericRowId(row.id))
        .filter((rowId): rowId is number => rowId !== null),
    };
  }

  private toDividedStandRow(
    detail: StandDivisionAndFusionStandDetailDto,
  ): IDividedStandRow | null {
    if (!detail?.id) {
      return null;
    }

    return {
      ...detail,
      id: detail.id,
      locationId: detail.locationId ?? undefined,
      propertyId: detail.propertyId ?? undefined,
      name: detail.name ?? String(detail.id),
      currentArea: detail.standArea ?? 0,
      newArea: detail.newArea ?? this.newAreaInput() ?? 0,
      sectionedArea:
        detail.sectionedArea ?? this.roundArea((detail.standArea ?? 0) - (detail.newArea ?? 0)),
      gisArea: this.gisArea(),
      standTypeId: detail.standTypeId ?? undefined,
      standTypeName: detail.standTypeName ?? null,
      hasNearbyPopulation: !!detail.hasNearbyPopulation,
      forestrySituationName: detail.forestrySituationName ?? null,
      cycleValue: detail.cycleValue ?? null,
      cycle: this.formatNumericValue(detail.cycleValue),
      isLastCycle: !!detail.isLastCycle,
      rotationValue: detail.rotationValue ?? null,
      rotation: this.formatNumericValue(detail.rotationValue),
      isLastRotation: !!detail.isLastRotation,
      plantation: detail.plantation ?? null,
      cycleRotation: detail.cycleRotation ?? null,
      species: detail.plantation?.speciesName ?? null,
      landContract: detail.cycleRotation?.landContractNumber ?? null,
      forestContract: detail.cycleRotation?.fomentContractNumber ?? null,
      isOriginal: this.selectedRows().length === 0,
    };
  }

  private hasDifferentReferenceValues(row: IDividedStandRow): boolean {
    const referenceRow = this.referenceRow();
    if (!referenceRow) {
      return false;
    }

    return (
      !this.sameNullableValue(referenceRow.species ?? null, row.species ?? null) ||
      !this.sameNullableValue(referenceRow.landContract ?? null, row.landContract ?? null) ||
      !this.sameNullableValue(referenceRow.forestContract ?? null, row.forestContract ?? null)
    );
  }

  private sameNullableValue(left: string | null, right: string | null): boolean {
    return (left ?? '').trim() === (right ?? '').trim();
  }

  private clearSelection(): void {
    this.selectedStandId.set(null);
    this.newAreaInput.set(null);
    this.selectedStandDetail.set(null);

    queueMicrotask(() => {
      this.standSelect()?.writeValue(null);
    });
  }

  private parseArea(value: AreaInputValue): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed =
      typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }

    return this.roundArea(parsed);
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private normalizeRows(rows: IDividedStandRow[]): IDividedStandRow[] {
    return rows.map((row, index) => ({
      ...row,
      isOriginal: index === 0,
    }));
  }

  private numericPropertyId(): number | null {
    const value = Number(this.propertyId());
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  private numericSelectedStandId(): number | null {
    const value = Number(this.selectedStandId());
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  private toNumericRowId(value: number | string): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  private toSingleSelectValue(value: SelectValue): string | null {
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }

    return value || null;
  }

  private formatNumericValue(value: number | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    return String(value);
  }

  private handleValidationError(error: unknown): void {
    if (this.shouldIgnoreValidationError(error)) {
      return;
    }

    const message = extractBackendErrorMessage(error);
    if (!message) {
      return;
    }

    if (VALIDATION_ERROR_KEYS.has(message)) {
      this.notifyError(message);
      return;
    }

    this.toaster.error(message, '', { life: DEFAULT_TOASTER_LIFE });
  }

  private handleAddStandError(error: unknown): void {
    if (this.shouldIgnoreValidationError(error) || this.isHandledByZnErrorHandler(error)) {
      return;
    }

    this.handleValidationError(error);
  }

  private shouldIgnoreValidationError(error: unknown): boolean {
    const code = extractBackendErrorCode(error);
    return !!code && IGNORED_BACKEND_ERROR_CODES.has(code);
  }

  private isHandledByZnErrorHandler(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      [400, 403, 404, 409, 500].includes(error.status)
    );
  }

  private notifyError(key: string): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivisionAndFusion.StepSelectStandsToDivide.${key}`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }

}




